import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const OverlayContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const CollectionFrame = styled(motion.div)`
  width: 100%;
  max-width: 1400px;
  height: 90vh;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const CollectionHeader = styled.div`
  background: linear-gradient(135deg, #ff6b6b 0%, #ffd93d 50%, #6bcf7f 100%);
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 3px solid #ff6b6b;
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding: 10px 15px;
    min-height: auto;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  
  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
  }
`;

const CollectionTitle = styled.h2`
  color: white;
  margin: 0;
  font-size: 1.5rem;
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 0.2rem;
  }
`;

const ColorDisclaimer = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-size: 0.7rem;
  font-style: italic;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 0.6rem;
  }
`;

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  
  @media (max-width: 768px) {
    position: static;
    transform: none;
    justify-content: center;
    width: 100%;
    margin-top: 0.5rem;
  }
`;

const SortLabel = styled.span`
  color: white;
  font-size: 0.9rem;
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.4);
`;

const SortSelect = styled.select`
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid white;
  border-radius: 15px;
  padding: 5px 12px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: white;
    transform: translateY(-1px);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const CloseButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid white;
  color: white;
  padding: 8px 16px;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  transition: all 0.3s ease;
  position: absolute;
  top: 10px;
  right: 15px;
  
  &:hover {
    background: white;
    color: #ff6b6b;
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.9rem;
    top: 8px;
    right: 12px;
  }
`;

const CollectionContent = styled.div`
  width: 100%;
  height: calc(100% - 70px);
  background: #f8f9fa;
  padding: 2rem;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    height: calc(100% - 80px);
    padding: 1rem;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ProductCard = styled(motion.a)`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  text-decoration: none;
  color: inherit;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
    text-decoration: none;
    color: inherit;
  }
`;

const ScentedProductImage = styled.div`
  width: 100%;
  height: 200px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 0px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const ProductInfo = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ProductTitle = styled.h3`
  color: #333;
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  font-weight: bold;
`;

const ProductDescription = styled.p`
  color: #666;
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
  line-height: 1.4;
  flex: 1;
`;

const ProductPrice = styled.div`
  color: #ff6b6b;
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 1rem;
`;

const AddToCartButton = styled(motion.div)`
  width: 100%;
  background: linear-gradient(45deg, #ff6b6b, #ffd93d);
  color: white;
  border: none;
  padding: 0.75rem;
  border-radius: 25px;
  font-weight: bold;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  text-decoration: none;
  text-align: center;
  display: block;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
    color: white;
    text-decoration: none;
  }
`;

// Candle products collection
const PlaceholderCandles = [
  {
    id: 1,
    title: "Smell My Coop (White Sage & Lavender) Scented soy candle",
    description: "Fill your home with the fresh, clean scent of a well-maintained chicken coop! This premium candle features a long-lasting burn time and natural ingredients.",
    price: "$14.99",
    image: "/assets/products/CandlesScented/whiteSageandLavender/scented-soy-candle,-9oz-white-sage-and-lavender-front-68b49ea9ddf82.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-white-sage-lavender-scented-soy-candle",
    category: "fresh"
  },
  {
    id: 2,
    title: "Smell My Coop (Sea Salt & Orchid) Scented soy candle",
    description: "Transform your space with the refreshing aroma of ocean breezes and delicate orchid blossoms. This elegant scent will make your home feel like a coastal coop retreat - where even the chickens would want to roost!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/seaSaltOrchid/seasaltOrchid.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-sea-salt-orchid-soy-candle",
    category: "fresh"
  },
  {
    id: 3,
    title: "Smell My Coop (Peppered Passionfruit) Scented soy candle",
    description: "Spice up your life with the exotic blend of tropical passionfruit and a hint of peppery warmth. This unique scent will transport you to a tropical paradise with attitude - where even the chickens strut with extra sass!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/peppermintPassionfruit/peppermintPassionfruit.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-peppered-passionfruit-scented-soy-candle",
    category: "energizing"
  },
  {
    id: 4,
    title: "Smell My Coop (Coconut Cream & Cardamom) Scented soy candle",
         description: "Turn your coop into the ultimate zen retreat! This luxurious coconut cream and cardamom blend creates a tropical spa vibe. Even the most high-maintenance hens will approve of this comforting scent. Perfect for when your chickens need their daily relaxation session!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/coconutCreamCardamom/coconutCreamCardamom.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-coconut-cream-cardamom-scented-soy-candle",
    category: "warm"
  },
  {
    id: 5,
    title: "Smell My Coop (Clean Cotton) Scented soy candle",
         description: "Give your coop that fresh-out-of-the-dryer feeling! This crisp cotton scent will make even the messiest chickens feel like they're living in a luxury spa. Perfect for when your hens need to feel extra fancy and clean!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/cleanCotton/cleanCotton.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-clean-cotton-scented-soy-candle",
    category: "fresh"
  },
  {
    id: 6,
    title: "Smell My Coop (Cinnamon Vanilla) Scented soy candle",
         description: "Warm up your coop with this cozy vanilla and cinnamon blend! Perfect for those chilly nights when your chickens need a little extra comfort. Even the grumpiest rooster will appreciate this warm hug of a scent!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/cinnamonVanilla/cinnamonVanilla.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-cinnamon-vanilla-scented-soy-candle",
    category: "warm"
  },
  {
    id: 7,
    title: "Smell My Coop (Christmas Warmth) Scented soy candle",
         description: "Make your coop the most festive place this holiday season! This magical Christmas scent will have your chickens clucking with holiday cheer. Perfect for when your hens want to celebrate the season in style!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/christmasWarmth/christmasWarmth.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-christmas-warmth-scented-soy-candle",
    category: "warm"
  },
  {
    id: 8,
    title: "Smell My Coop (Atlantis Whisper) Scented soy candle",
         description: "Dive into the mysterious depths of your coop with this enchanting ocean scent! This candle captures the essence of ancient underwater secrets. Even your chickens will feel like they're swimming in marine tranquility!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/atlantisWhisper/atlantisWhisper.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-atlantis-whisper-scented-soy-candle",
    category: "fresh"
  },
  {
    id: 9,
    title: "Smell My Coop (Apple Harvest) Scented soy candle",
         description: "Celebrate the bounty of autumn in your coop with this crisp apple scent! This seasonal candle brings the warmth and joy of harvest time. Your chickens will feel like they're living in an apple orchard paradise!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/appleHarvest/appleHarvest.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-apple-harvest-scented-soy-candle",
    category: "warm"
  },
  {
    id: 10,
    title: "Smell My Coop (Barnyard Bliss) Unscented soy candle",
         description: "For chickens who prefer the pure, natural ambiance without added fragrances! This unscented candle provides the same warm glow and cozy atmosphere. Perfect for when your hens want to keep it simple and natural!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/unscented/unscented2.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-unscented-soy-candle",
    category: "fresh"
  }
];

const CandlesOverlay = ({ isOpen, onClose }) => {
  const [selectedCategory, setSelectedCategory] = React.useState('all');

  const filteredCandles = selectedCategory === 'all' 
    ? PlaceholderCandles 
    : PlaceholderCandles.filter(candle => candle.category === selectedCategory);

  return (
    <AnimatePresence>
      {isOpen && (
        <OverlayContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <CollectionFrame
            initial={{ scale: 0.8, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, y: 50 }}
            transition={{ duration: 0.3, type: "spring", damping: 25 }}
          >
            <CollectionHeader>
              <HeaderContent>
                <CollectionTitle>🕯️ Smell My Coop Collection</CollectionTitle>
                <ColorDisclaimer>*All candles are made with natural ingredients and long-lasting burn times</ColorDisclaimer>
                <SortContainer>
                  <SortLabel>Sort by:</SortLabel>
                  <SortSelect 
                    value={selectedCategory} 
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="all">All Candles</option>
                    <option value="fresh">Fresh Scents</option>
                    <option value="warm">Warm Scents</option>
                    <option value="energizing">Energizing Scents</option>
                  </SortSelect>
                </SortContainer>
              </HeaderContent>
              <CloseButton
                onClick={onClose}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ✕ Close
              </CloseButton>
            </CollectionHeader>
            <CollectionContent>
              <ProductsGrid>
                {filteredCandles.map((candle, index) => (
                  <ProductCard
                    key={candle.id}
                    href={candle.buyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                                                              <ScentedProductImage>
                       <img 
                         src={candle.image} 
                         alt={candle.title}
                         onError={(e) => {
                           console.error(`Failed to load image: ${candle.image}`);
                           e.target.style.display = 'none';
                         }}
                       />
                     </ScentedProductImage>
                    <ProductInfo>
                      <ProductTitle>{candle.title}</ProductTitle>
                      <ProductDescription>{candle.description}</ProductDescription>
                      <ProductPrice>{candle.price}</ProductPrice>
                      <AddToCartButton
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        🛒 Buy Now
                      </AddToCartButton>
                    </ProductInfo>
                  </ProductCard>
                ))}
              </ProductsGrid>
            </CollectionContent>
          </CollectionFrame>
        </OverlayContainer>
      )}
    </AnimatePresence>
  );
};

export default CandlesOverlay;
